/**
 * Who decides on a pending student.
 *
 * A student asks for a class at signup. The teachers of that class approve
 * them; a student who asked for no class, or for a class with no teacher, is
 * for the school's staff to place. Mirrors requestedClassOf in
 * functions/src/services/schoolRoster.ts, which is what the server enforces.
 */

export interface StudentLike {
  id: string;
  approvalStatus: string | null;
  classIds: string[];
  requestedClassId: string | null;
}

export interface ClassLike {
  id: string;
  teacher_ids?: string[];
  archived?: boolean;
}

export const isPending = (s: Pick<StudentLike, 'approvalStatus'>) => (s.approvalStatus ?? 'pending') === 'pending';

/** The class asked for at signup; older signups wrote it into class_ids. */
export function requestedClassOf(s: Pick<StudentLike, 'requestedClassId' | 'classIds'>): string | null {
  return s.requestedClassId ?? s.classIds[0] ?? null;
}

/** Pending students who asked for one of these classes — the teacher's to decide. */
export function pendingForTeacher<T extends StudentLike>(students: T[], teacherClassIds: Iterable<string>): T[] {
  const mine = new Set(teacherClassIds);
  return students.filter((s) => {
    if (!isPending(s)) return false;
    const wanted = requestedClassOf(s);
    return wanted !== null && mine.has(wanted);
  });
}

/**
 * Pending students no teacher can decide on: they asked for no class, for a
 * class that no longer exists, or for one nobody teaches.
 */
export function pendingForSchool<T extends StudentLike>(students: T[], classes: ClassLike[]): T[] {
  const taught = new Set(classes.filter((c) => c.archived !== true && (c.teacher_ids ?? []).length > 0).map((c) => c.id));
  return students.filter((s) => {
    if (!isPending(s)) return false;
    const wanted = requestedClassOf(s);
    return wanted === null || !taught.has(wanted);
  });
}
